<template>
  <section class="main">
    <h1 class="intro">{{ $t('CONFIGURE_FEATURES') }}</h1>
    <div class="row">
      <loader v-if="!inited"/>
      <form class="features" v-if="inited">


        <h2 class="heading">{{ $t('GENERAL DEVICE CONFIGURATION') }}</h2>
        <div class="feature" v-for="(label, feature) in basic" :key="feature">
          <p class="feature__text g-text" v-html="label"></p>
          <yes-no class="feature__select" v-model="config[feature]"/>
        </div>
        <div class="feature">
          <p class="feature__text g-text">
            {{ $t('FREE_CERTIFICATE_FOR_SIGNING') }}
          </p>
          <template v-if="enterpriseCertificates.length===0">
            <html-with-links tag="span"
                             :html="$t('FREE_SIGNINIG_IS_NOT_AVAILABLE')"></html-with-links>
          </template>
          <template v-if="enterpriseCertificates.length">
          <yes-no
            class="feature__select"
            :options="enterpriseCertificates"
            v-model="config.enterprise_cert_id"
            vertical
          /></template>
        </div>
        <p class="warning">
          {{ $t('ADVANCED_USAGE_ZONE_NOTICE') }}
        </p>
        <div
          class="feature"
          v-for="(label, feature) in advanced"
          :key="feature"
        >
          <p class="feature__text g-text" v-html="label"></p>
          <yes-no class="feature__select" v-model="config[feature]"/>
        </div>
        <div class="feature">
          <p class="feature__text g-text">
            {{ $t('IDENTITY_TYPE_TO_USE_FOR_SIGNING') }}
          </p>
          <yes-no
            class="feature__select"
            :options="{
              auto: $t('AUTO'),
              development: $t('DEVELOPMENT'),
              distribution: $t('distribution'),
            }"
            v-model="config.signing_identity_type"
          />
        </div>

        <h2
          class="heading"
          v-html="
            $t('CONFIGURE_DEV_CERTIFICATE_TITLE')
          "
        ></h2>

        <div v-if="devCredentialsProviders.length" class="inline-buttons">
          <template v-for="provider in devCredentialsProviders">
            <btn
              type="primary"
              @click.native.prevent="importDevCredetials(provider.href)"
              :key="provider.name"
            >{{ $t('IMPORT_FROM', { from: provider.name }) }}
            </btn>&nbsp;
          </template>
        </div>

        <html-with-links tag="div" style="text-align: center;"
                         :html="$t('INTEGRATE_EASY_LINKING_NOTICE',{link:'https://rtfm.dbservices.to/#/integrations/easy-certificate-linking'})"></html-with-links>

        <div class="feature">
          <p class="feature__text g-text">
            {{ $t('DEV_CERTIFICATE_BUNDLE') }} (development)
            <span class="feature__info" v-if="details.dev_p12">{{
                $t('ATTACHED')
              }}</span>
            <span class="feature__info" style="color: green"
                  v-if="details.dev_p12_validation_result===true">{{
                $t('VALID')
              }}</span>
            <span class="feature__info" style="color: orange"
                  v-if="details.dev_p12_validation_issues.length">{{
                $t('ISSUES')
              }}: {{ details.dev_p12_validation_issues.join(', ') }}</span>
            <span class="feature__info" style="color: red"
                  v-if="details.dev_p12_validation_result===false">{{
                $t('INVALID')
              }}, {{ details.dev_p12_validation_error }}</span>
          </p>
          <file v-model="config.dev_p12" class="feature__file"/>
        </div>
        <div class="feature">
          <p class="feature__text g-text">
            {{ $t('DEV_CERTIFICATE_BUNDLE_PASSWORD') }} (development)
          </p>
          <input
            v-model="config.dev_p12_password"
            placeholder="Enter password"
            class="feature__input"
            type="text"
          />
        </div>
        <div class="feature">
          <p class="feature__text g-text">
            {{ $t('MOBILE_PROVISIONING_PROFILE') }} (development, explicit)
            <span class="feature__info" v-if="details.dev_provision">{{
                $t('ATTACHED')
              }}</span>
            <span class="feature__info" style="color: green;"
                  v-if="details.dev_provision_validation_result===true">{{
                $t('VALID')
              }}</span>
            <span class="feature__info" style="color: orange;"
                  v-if="details.dev_provision_validation_issues.length">{{
                $t('ISSUES')
              }}: {{
                details.dev_provision_validation_issues.join(', ')
              }}</span>

            <span class="feature__info" style="color: red;"
                  v-if="details.dev_provision_validation_result===false">{{
                $t('INVALID')
              }}, {{ details.dev_provision_validation_error }}</span>
          </p>
          <file v-model="config.dev_provision" class="feature__file"/>
        </div>
        <div class="feature">
          <p class="feature__text g-text">
            {{ $t('MOBILE_PROVISIONING_PROFILE') }} (development, wildcard)
            <span class="feature__info" v-if="details.dev_wildcard_provision">{{
                $t('ATTACHED')
              }}</span>
            <span class="feature__info" style="color: green;"
                  v-if="details.dev_wildcard_provision_validation_result===true">{{
                $t('VALID')
              }}</span>
            <span class="feature__info" style="color: orange;"
                  v-if="details.dev_wildcard_provision_validation_issues.length">{{
                $t('ISSUES')
              }}: {{
                details.dev_wildcard_provision_validation_issues.join(', ')
              }}</span>

            <span class="feature__info" style="color: red;"
                  v-if="details.dev_wildcard_provision_validation_result===false">{{
                $t('INVALID')
              }}, {{ details.dev_wildcard_provision_validation_error }}</span>
          </p>
          <file v-model="config.dev_wildcard_provision" class="feature__file"/>
        </div>
        <div class="feature">
          <p class="feature__text g-text">
            {{ $t('DEV_CERTIFICATE_BUNDLE') }} (distribution)
            <span class="feature__info" v-if="details.p12">{{
                $t('ATTACHED')
              }}</span>
            <span class="feature__info" style="color: green"
                  v-if="details.p12_validation_result===true">{{
                $t('VALID')
              }}</span>
            <span class="feature__info" style="color: orange"
                  v-if="details.p12_validation_issues.length===false">{{
                $t('ISSUES')
              }}: {{ details.p12_validation_issues.join(', ') }}</span>
            <span class="feature__info" style="color: red"
                  v-if="details.p12_validation_result===false">{{
                $t('INVALID')
              }}, {{ details.p12_validation_error }}</span>
          </p>
          <file v-model="config.p12" class="feature__file"/>
        </div>
        <div class="feature">
          <p class="feature__text g-text">
            {{ $t('DEV_CERTIFICATE_BUNDLE_PASSWORD') }} (distribution)
          </p>
          <input
            v-model="config.p12_password"
            placeholder="Enter password"
            class="feature__input"
            type="text"
          />
        </div>
        <div class="feature">
          <p class="feature__text g-text">
            {{ $t('MOBILE_PROVISIONING_PROFILE') }} (distribution, explicit)
            <span class="feature__info" v-if="details.provision">{{
                $t('ATTACHED')
              }}</span>
            <span class="feature__info" style="color: green;"
                  v-if="details.provision_validation_result===true">{{
                $t('VALID')
              }}</span>
            <span class="feature__info" style="color: orange;"
                  v-if="details.provision_validation_issues.length">{{
                $t('ISSUES')
              }}: {{
                details.provision_validation_issues.join(', ')
              }}</span>

            <span class="feature__info" style="color: red;"
                  v-if="details.provision_validation_result===false">{{
                $t('INVALID')
              }}, {{ details.provision_validation_error }}</span>
          </p>
          <file v-model="config.provision" class="feature__file"/>
        </div>
        <div class="feature">
          <p class="feature__text g-text">
            {{ $t('MOBILE_PROVISIONING_PROFILE') }} (distribution, wildcard)
            <span class="feature__info" v-if="details.wildcard_provision">{{
                $t('ATTACHED')
              }}</span>
            <span class="feature__info" style="color: green;"
                  v-if="details.wildcard_provision_validation_result===true">{{
                $t('VALID')
              }}</span>
            <span class="feature__info" style="color: orange;"
                  v-if="details.wildcard_provision_validation_issues.length">{{
                $t('ISSUES')
              }}: {{
                details.wildcard_provision_validation_issues.join(', ')
              }}</span>

            <span class="feature__info" style="color: red;"
                  v-if="details.wildcard_provision_validation_result===false">{{
                $t('INVALID')
              }}, {{ details.wildcard_provision_validation_error }}</span>
          </p>
          <file v-model="config.wildcard_provision" class="feature__file"/>
        </div>
        <div class="buttons">
          <button
            class="buttons__delete"
            @click.prevent="reset('clear_developer_entity')"
          >
            <loader v-if="pending" size="15px" border="3px" absolute/>
            {{ $t('CLEAR_DEVELOPER_IDENTITY') }}
          </button>
          <button class="buttons__save" @click.prevent="save">
            <loader v-if="pending" size="15px" border="3px" absolute/>
            {{ $t('SAVE_FEATURE_CONFIGURATION') }}
          </button>
        </div>
      </form>
      <html-with-links
        class="desc g-text"
        :html="
          '<p>' +
          $t('CONFIGURE_SUMMARY_DESC', {
            buyLink: '/my/buy',
            privacyLink: '/info/privacy',
          }) + '<br/><br/><a href=\'https://www.youtube.com/watch?v=cQiRDQ2ijGg\' target=\'_blank\'>'+$t('VIDEO_TUTORIAL')+'</a>'+'</p>'
        "
      ></html-with-links>
    </div>
  </section>
</template>

<script>
/* eslint-disable no-prototype-builtins */

import YesNo from '@/components/YesNo';
import Loader from '@/components/Loader';
import File from '@/components/Base64File';
import Btn from '@/components/Button';
import HtmlWithLinks from '@/components/HtmlWithLinks';

export default {
  components: {
    YesNo,
    Loader,
    File,
    HtmlWithLinks,
    Btn,
  },
  data() {
    return {
      inited: false,
      pending: false,
      config: {
        free_appleid: '',
        free_appleid_password: '',
        ignore_compatibility: 'no',
        ask_for_installation_options: 'no',
        enable_psychic_paper: 'no',
        enable_inapp_patch: 'no',
        enable_trainer: 'no',
        allow_malware_installations: 'no',
        p12: null,
        p12_password: null,
        provision: null,
        wildcard_provision: null,
        dev_p12: null,
        dev_p12_password: null,
        dev_provision: null,
        dev_wildcard_provision: null,
        enterprise_cert_id: '0',
        model: '',
        signing_identity_type: 'auto',
        ios_version: '',
        is_opted_out_from_emails: 'no',
      },
      details: {
        is_apple_silicon: '',
        plus_account: {
          status: '',
        },
        ios_version: '',
        free_last_error: null,
        free_appleid: '',
        free_appleid_password: '',
        p12: '',
        provision: '',
        wildcard_provision: '',
        provision_validation_result: false,
        provision_validation_error: '',
        wildcard_provision_validation_result: false,
        wildcard_provision_validation_error: '',
        p12_validation_result: false,
        p12_validation_error: '',
        p12_validation_issues: [],
        provision_validation_issues: [],
        wildcard_provision_validation_issues: [],
        dev_p12: '',
        dev_provision: '',
        dev_wildcard_provision: '',
        dev_provision_validation_result: false,
        dev_provision_validation_error: '',
        dev_wildcard_provision_validation_result: false,
        dev_wildcard_provision_validation_error: '',
        dev_p12_validation_result: false,
        dev_p12_validation_error: '',
        dev_p12_validation_issues: [],
        dev_provision_validation_issues: [],
        dev_wildcard_provision_validation_issues: [],
      },
      devCredentialsProviders: [],
      enterpriseCertificates: [],
    };
  },
  computed: {
    title() {
      return this.$t('CONFIGURE_FEATURES');
    },
    majorIOSVersion() {
      if (!this.config.ios_version) {
        return 0;
      }

      return +this.config.ios_version.split('.')[0];
    },
    basic() {
      return {
        ignore_compatibility: this.$t('FORCE_APP_INSTALLATION'),
        ask_for_installation_options: this.$t('ASK_FOR_CUSTOM_INSTALLATION'),
        is_opted_out_from_emails: this.$t('OPT_OUT_DESCRIPTION'),
      };
    },
    advanced() {
      return {
        allow_malware_installations: this.$t('ALLOW_MALWARE_INSTALLATIONS'),
      };
    },
  },
  methods: {
    save() {
      if (this.pending) {
        return;
      }

      this.pending = true;
      this.$api.saveConfiguration(this.config)
        .then(() => {
          this.$notify({
            type: 'success',
            text: this.$t('FEATURE_CONFIGURATION_SAVED'),
          });
        })
        .then(this.getConfig)
        .then(() => new Promise((resolve) => {
          setTimeout(() => {
            this.$store.dispatch('getDevices')
              .finally(resolve);
          }, 300);
        }))
        .finally(() => {
          this.pending = false;
        });
    },
    reset(param) {
      if (this.pending) {
        return;
      }

      this.pending = true;
      this.$api.saveConfiguration({ [param]: 'yes' })
        .then(this.getConfig)
        .finally(() => {
          this.pending = false;
        });
    },
    getConfig() {
      return this.$store.dispatch('getConfig')
        .then((config) => {
          Object.keys(config)
            .forEach((feature) => {
              if (this.config.hasOwnProperty(feature) && this.config[feature] !== null) {
                this.config[feature] = config[feature];
              }
              if (this.details.hasOwnProperty(feature)) {
                this.details[feature] = config[feature];
              }
            });
          this.inited = true;
        });
    },
    importDevCredetials(href) {
      this.$confirm(this.$t('IMPORT_DEV_CREDENTIALS_PROVIDER_CONFIRMATION'))
        .then(() => {
          window.location.href = href;
        });
    },
  },
  created() {
    this.getConfig();

    this.$api.getDevCredentialsProviders()
      .then((response) => {
        this.devCredentialsProviders = response;
      });
    this.$api.getEnterpriseCertificates()
      .then((response) => {
        this.enterpriseCertificates = [];
        Object.values(response)
          .forEach((cert) => {
            this.enterpriseCertificates.push({ value: cert.id, label: cert.name });
          });
      });
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.main {
  display: block;
  flex-grow: 1;
}

.feature {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #e5e5e5;

  &__info {
    display: block;
    font-size: 12px;
  }

  &__text {
    margin-right: 60px;
  }

  &__select,
  &__file {
    margin-left: auto;
  }

  &__input {
    background: var(--tertiary-bg);
    border: 1px solid var(--tertiary-accent);
    border-radius: 2px;
    padding: 6px;
    margin-left: auto;
    flex: 0 0 150px;
    width: 150px;
    font-size: 14px;
    font-family: "IBM Plex Sans", sans-serif;
    color: var(--text-primary);

    &::placeholder {
      font-family: "IBM Plex Sans", sans-serif;
      color: var(--text-primary);
    }
  }
}

.warning {
  color: var(--configure-error-text);
  background: var(--bg-error);
  border-radius: 2px;
  padding: 3px 10px;
  text-align: center;
  line-height: 18px;
  margin: 20px 0;

  @include notebook {
    margin: 10px 0;
  }
}

.heading {
  color: var(--text-tertiary);
  font-weight: 600;
  margin: 25px 0 15px;

  &:first-child {
    margin-top: 0;
  }

  @include notebook {
    margin: 15px 0 10px;
    line-height: 16px;
  }
}

.buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 15px;

  @include tablet {
    flex-direction: column;
  }

  &_sa {
    justify-content: space-around;
  }

  &__delete,
  &__save {
    position: relative;
    padding: 5px 15px;
    font-size: 14px;
    border-radius: 2px;

    @include notebook {
      padding: 5px 8px;
    }

    @include tablet {
      &:not(:last-child) {
        margin-bottom: 10px;
      }
    }
  }

  &__delete {
    color: var(--text-primary);
    border: 1px solid rgba(250, 101, 101, 0.5);
  }

  &__save {
    background: $primary;
    color: #fff;
  }
}

.row {
  display: flex;
  align-items: flex-start;

  @include notebook {
    flex-direction: column-reverse;
  }
}

.features,
.desc {
  @include notebook {
    width: 100%;
  }
}

.desc {
  background: var(--secondary-bg);
  border-radius: 8px;
  flex: 0 0 200px;
  margin-left: 40px;
  padding: 10px;

  @include notebook {
    margin: 0 0 15px;
    flex: 0 0 auto;
  }

  &::v-deep {
    p:not(:last-child) {
      margin-bottom: 15px;
    }
  }
}

.inline-buttons {
  line-height: 33px;
  margin-top: -5px;

  .button {
    display: inline;
  }
}
</style>
